import { MatchEvent } from 'shared/types/match-event';
// Не баррель `shared/utils`: он тянет макросы Lingui, а домен модуля грузит и нарезчик (tsx без Babel).
import { TransformUrl } from 'shared/utils/transform-url/transform-url';
import { DEFAULT_HIGHLIGHT_WINDOWS } from 'modules/video-markers/domain/constants';
import { HighlightWindowSettings, VideoMarker } from 'modules/video-markers/domain/interfaces';
import { selectRecordedEvents } from './match-recording-file';
import { resolveEventVideoPosition } from './resolve-event-video-position';

/**
 * Насколько позиция в записи эфира VK отстаёт от позиции эфира OBS (`streaming_ms`): позиция в
 * VOD = `streaming_ms` + сдвиг. Замер один — тестовый эфир 09.10.2026 (`.claude/docs/video-markers.md`,
 * «Нарезка из записи эфира VK»), постоянство не доказано, поэтому это дефолт параметра, а не закон.
 */
export const VK_VOD_OFFSET_MS = -8_230;

export type MomentEvent = Pick<MatchEvent, 'id' | 'period' | 'seconds' | 'type'>;

export interface BuildMomentUrlsParams {
  events: readonly MomentEvent[];
  /** Все строки `video_markers` матча — любых программ. */
  markers: readonly VideoMarker[];
  /** Ссылка на трансляцию матча (VK или YouTube), как её вставил организатор. */
  streamUrl: string;
  windows?: HighlightWindowSettings;
  /** Сдвиг записи эфира VK; у YouTube сдвиг не замерян и считается нулевым. */
  vkVodOffsetMs?: number;
}

/**
 * Строки эфира: только с `streaming_ms` и только одной программы — формула позиции работает на
 * строках одной программы. Если эфир вели несколько (прогон IMGM-1484 — OBS и vMix), берётся та,
 * у которой таких строк больше.
 *
 * Строки без `streaming_ms` отбрасываются и у выбранной программы: матч 63766 та же OBS писала и
 * без эфира (id 205–299), и их метка по получению с тем же типом и секундой забрала бы событие у
 * метки эфира, а событие, полученное, пока эфира не было, считалось бы от опор чужого эфира.
 */
function markersOfStream(markers: readonly VideoMarker[]): VideoMarker[] {
  const streamed = markers.filter((row) => row.streamingMs !== null);
  const rowsByRecorder = new Map<VideoMarker['recorder'], number>();
  for (const row of streamed) rowsByRecorder.set(row.recorder, (rowsByRecorder.get(row.recorder) ?? 0) + 1);

  const [recorder] = Array.from(rowsByRecorder.entries()).sort((a, b) => b[1] - a[1])[0] ?? [];
  return streamed.filter((row) => row.recorder === recorder);
}

/**
 * Ссылки «Моментов» из видеометок: id события → ссылка на запись эфира с таймкодом.
 *
 * Позиция — шкала `streaming` (`resolveEventVideoPosition`), а не начало тайма, которое прежде
 * вводил организатор: перерыв между таймами и несколько матчей в одном видео ссылку не сдвигают.
 * Из позиции вычитается запас «до» из окон клипов — статист вносит событие после момента — и
 * прибавляется сдвиг записи VK.
 *
 * События бэкенда и метки журнала сопоставляются через `selectRecordedEvents`: по id, а если он
 * другой — по типу, тайму и секунде. В живых данных id не совпадают никогда: у журнала они
 * клиентские (`1791437479690`), у бэкенда — свои. Событие без метки, без позиции на шкале эфира или
 * площадка, которую `TransformUrl` не узнал, — ссылки нет, и прежняя ссылка события не трогается.
 */
export function buildMomentUrlsFromMarkers({
  events,
  markers,
  streamUrl,
  windows = DEFAULT_HIGHLIGHT_WINDOWS,
  vkVodOffsetMs = VK_VOD_OFFSET_MS,
}: BuildMomentUrlsParams): Map<number, string> {
  const urls = new Map<number, string>();
  const url = streamUrl.trim();
  const platform = TransformUrl.getDomain(url);
  if (platform === null) return urls;

  const rows = markersOfStream(markers);
  const offsetMs = platform === 'vk' ? vkVodOffsetMs : 0;
  const recorded = selectRecordedEvents(
    events.map((event) => ({ ...event, momentEventId: event.id })),
    rows,
  );

  for (const event of recorded) {
    const { positionMs } = resolveEventVideoPosition(event, rows, 'streaming');
    if (positionMs === null) continue;

    const { beforeMs } = windows.byEventType[event.type] ?? windows.fallback;
    // Не меньше секунды: у генераторов `TransformUrl` нулевой таймкод даёт `…undefined` в ссылке.
    const startSeconds = Math.max(Math.floor((positionMs + offsetMs - beforeMs) / 1000), 1);

    const momentUrl =
      platform === 'vk'
        ? TransformUrl.vk.generateUrl({ ...pickVkIds(url), totalSeconds: startSeconds })
        : TransformUrl.youtube.generateUrl({ videoId: TransformUrl.youtube.getUrlData(url).videoId, start: startSeconds });
    if (momentUrl) urls.set(event.momentEventId, momentUrl);
  }

  return urls;
}

function pickVkIds(url: string) {
  const { oid, id } = TransformUrl.vk.getUrlData(url);
  return { oid, id };
}
